import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CodeInputComponent } from 'angular-code-input';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { HelperService } from '../../core/services/helper.service';
import { AuthErrors, AuthErrorService, AuthService, PnpLoginService, PnpUserService } from '@pn-plus/pnp-services';
import { IUserPasswordResetPayload } from 'libs/pnp-services/src/lib/api/user/pnp-user.model';
import { ILoginPinPayload } from 'libs/pnp-services/src/lib/api/login/pnp-login.model';
import { LoginService } from '../../core/services/login.service';
import { FingerprintService } from '../../core/services/fingerprint.service';

@Component({
    selector: 'nrwl-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit {
    @ViewChild('codeInput') codeInput !: CodeInputComponent;
    @ViewChild('passwordInput') passwordInput: ElementRef<HTMLInputElement>;
    
    public formGroup: FormGroup;
    public codeComplete = '';
    public isLoading = false;
    public activeError: any;

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
        @Optional() public dialogRef: MatDialogRef<ResetPasswordComponent>,
        private helperService: HelperService,
        private formBuilder: FormBuilder,
        public authService: AuthService,
        private router: Router,
        public sanitizer: DomSanitizer,
        public authErrorService: AuthErrorService,
        private pnpLoginService: PnpLoginService,
        private pnpUserService: PnpUserService,
        private loginService: LoginService,
        private fingerprintService: FingerprintService
    ) { }

    ngOnInit(): void {
        this.formGroup = this.formBuilder.group(
            {
                practiceId: ['practice_login', Validators.required],
                user: ['user_login', Validators.required]
            }
        );
    }

    onClose() {
        this.dialogRef.close();
    }

    resetPassword(practiceId: string, user: string): Observable<any> {
        const payload: IUserPasswordResetPayload = {
            practice: practiceId,
            user: user,
            deviceId: `${this.fingerprintService.getFingerPrint()}`
        }
        return this.pnpUserService.putUserPasswordReset(payload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response: any) => {
                    return response;
                })
            )
    }

    async onPasswordSubmit() {

        this.setBusy(true);
        const response = await firstValueFrom(this.resetPassword(this.formGroup.value.practiceId, this.formGroup.value.user));
        this.setBusy(false);

        if(this.activeError) {
            return;
        }
        this.dialogRef.close(response);

    }

    async onCodeCompleted(event: string) {
        this.setBusy(true);
        const loginResponse = await firstValueFrom(this.verifyPin(event));
        if(loginResponse) {
            this.data.loginResponse.accessToken = loginResponse;
            this.authService.updateAuthModel(this.data);
            this.codeInput.reset();
            this.codeInput.reset();
            this.dialogRef.close();
            // TODO: Check Login Diagram, 1 screen might be missing
            this.loginService.navigate('/login');
        }
        this.codeInput.reset();
        this.setBusy(false);
    }

    getErrorByType(errorType: any) {
        let error;
        switch(errorType) {
            case 'UserNotValid': {
                error = AuthErrors.UserNotValid;
                break;
            }
            case 'PracticeNotValid': {
                error = AuthErrors.PracticeNotValid;
                break;
            }
            case 'UserHasNoConfirmedCellPhone': {
                error = AuthErrors.UserHasNoConfirmedCellPhone;
                break;
            }
            default: {
                error = AuthErrors.Default;
            }
        }
        return error;
    }

    verifyPin(pin: string): Observable<any> {
        const authModel = this.authService.getAuthModel();
        const payload: ILoginPinPayload = {
            pinToken: <any>authModel?.loginResponse?.pinToken?.jwt,
            pin: pin,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
        return this.pnpLoginService.postLoginPin(payload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response: any) => {
                    return response;
                })
            )
    }

    onCodeChanged(event: any) {
        this.activeError = null;
        this.helperService.showPreviousCodeInput(event);
    }

    public toggleToPasswordForm() {
        this.formGroup.reset();
        this.activeError = null;
    }

    onResetPasswordClicked() {
        // TODO: Reset password
        console.log('TODO: Navigate to reset password form');
    }
    onLoginWithPasswordClicked() {
        this.toggleToPasswordForm()
    }
    onLoginClicked() {
        this.dialogRef.close();
    }
    
    public getHtml(template: any) {
        template = template.replace(/&lt;/g, "<").replace(/&gt;/g,">");
        return this.sanitizer.bypassSecurityTrustHtml(template);
    }

    setBusy(flag: boolean): void {
        this.isLoading = flag;
    }
}
